import { useState, useRef, useEffect, useCallback, memo } from "react";
import { useSelector } from "react-redux";

// ─────────────────────────────────────────────
// Icon
// ─────────────────────────────────────────────

export const Icon = memo(
  ({ name, className = "text-[20px]", filled = false }) => (
    <span
      className={`material-symbols-outlined ${className}`}
      style={{ fontVariationSettings: filled ? "'FILL' 1" : "'FILL' 0" }}
      aria-hidden="true"
    >
      {name}
    </span>
  ),
);
Icon.displayName = "Icon";

// ─────────────────────────────────────────────
// Spinner
// ─────────────────────────────────────────────

export const Spinner = memo(({ size = "w-4 h-4", color = "text-white" }) => (
  <svg
    className={`animate-spin ${size} ${color}`}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
  </svg>
));
Spinner.displayName = "Spinner";

// ─────────────────────────────────────────────
// Button
// ─────────────────────────────────────────────

const BUTTON_VARIANTS = {
  primary:
    "px-6 py-3 bg-gradient-to-br from-[#005b60] to-[#08757c] text-white hover:shadow-lg",
  danger:
    "px-6 py-3 bg-gradient-to-br from-red-600 to-red-700 text-white hover:shadow-lg",
  ghost:
    "px-4 py-2 bg-white text-[#005b60] border border-[#005b60]/20 rounded-lg text-xs font-bold hover:bg-[#005b60]/5",
  neutral:
    "px-4 py-2 bg-[#e5e9e9] text-[#3e494a] rounded-lg hover:bg-[#d0d8d8]",
};

export const Button = memo(
  ({
    children,
    onClick,
    type = "button",
    form,
    variant = "primary",
    className = "",
    disabled = false,
  }) => (
    <button
      type={type}
      form={form}
      onClick={onClick}
      disabled={disabled}
      className={`
      inline-flex items-center justify-center gap-2 font-semibold rounded-xl transition-all
      focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#005b60]
      active:scale-95 disabled:opacity-50 disabled:pointer-events-none
      ${BUTTON_VARIANTS[variant] ?? BUTTON_VARIANTS.primary} ${className}
    `}
    >
      {children}
    </button>
  ),
);
Button.displayName = "Button";

// ─────────────────────────────────────────────
// IconButton
// ─────────────────────────────────────────────

export const IconButton = memo(
  ({
    icon,
    label,
    onClick,
    colorClass = "text-[#3e494a] hover:text-[#005b60]",
  }) => (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className={`p-2 ${colorClass} hover:bg-white rounded-lg transition-all shadow-none hover:shadow-sm focus-visible:ring-2 focus-visible:ring-[#005b60] focus-visible:outline-none`}
    >
      <Icon name={icon} />
    </button>
  ),
);
IconButton.displayName = "IconButton";

// ─────────────────────────────────────────────
// CancelButton
// ─────────────────────────────────────────────

export const CancelButton = memo(({ onClick, label = "Cancel" }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-slate-600 font-bold text-sm border border-slate-300 hover:bg-slate-50 hover:border-slate-400 transition-colors"
  >
    <span className="material-symbols-outlined text-lg">close</span>
    {label}
  </button>
));
CancelButton.displayName = "CancelButton";

// ─────────────────────────────────────────────
// PageHeading
// ─────────────────────────────────────────────

export const PageHeading = memo(({ title, subtitle, className = "" }) => (
  <div className={className}>
    <h2 className="text-3xl font-extrabold text-[#08757C] tracking-tight">
      {title}
    </h2>
    {subtitle && (
      <p className="text-[#3e494a] mt-2 max-w-xl text-sm">{subtitle}</p>
    )}
  </div>
));
PageHeading.displayName = "PageHeading";

// ─────────────────────────────────────────────
// Chip
// ─────────────────────────────────────────────

export const Chip = memo(
  ({ children, active = false, onClick, className = "" }) => (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`
      px-3 py-1.5 rounded-full text-xs font-semibold border transition-all
      ${active ? "bg-[#08757C] text-white border-[#08757C]" : "bg-white text-[#3e494a] border-[#cad6d6] hover:bg-[#e5e9e9]"}
      ${className}
    `}
    >
      {children}
    </button>
  ),
);
Chip.displayName = "Chip";

// ─────────────────────────────────────────────
// StatusChip
// ─────────────────────────────────────────────

const STATUS_COLORS = {
  stable: "bg-[#e5f7f6] text-[#08757C]",
  error: "bg-red-50 text-red-700",
  secondary: "bg-purple-50 text-purple-700",
  warning: "bg-amber-50 text-amber-700",
};

export const StatusChip = memo(({ label, variant = "stable" }) => (
  <span
    className={`px-3 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[variant] ?? STATUS_COLORS.stable}`}
  >
    {label}
  </span>
));
StatusChip.displayName = "StatusChip";

// ─────────────────────────────────────────────
// FormField
// ─────────────────────────────────────────────

export const FormField = memo(
  ({ id, label, required = false, hint, error, children }) => (
    <div className="space-y-1.5">
      {label && (
        <label
          htmlFor={id}
          className="block text-sm font-semibold text-[#181c1d]"
        >
          {label}
          {required && (
            <span className="text-red-600 ml-1" aria-label="required">
              *
            </span>
          )}
        </label>
      )}
      {children}
      {hint && !error && (
        <p className="text-[11px] text-[#6e797a] italic">{hint}</p>
      )}
      {error && (
        <p id={`${id}-error`} className="text-xs text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  ),
);
FormField.displayName = "FormField";

// ─────────────────────────────────────────────
// TextInput
// ─────────────────────────────────────────────

export const TextInput = memo(
  ({
    id,
    value,
    onChange,
    placeholder = "",
    error,
    disabled = false,
    className = "",
    ...rest
  }) => (
    <input
      id={id}
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      aria-invalid={!!error}
      aria-describedby={error ? `${id}-error` : undefined}
      className={`
      block w-full px-4 py-3 bg-[#e5e9e9] border-none rounded-xl
      text-[#181c1d] placeholder:text-[#bdc9c9] outline-none
      focus:bg-white focus:ring-2 transition-all
      disabled:opacity-50 disabled:cursor-not-allowed
      ${error ? "focus:ring-red-400" : "focus:ring-[#005b60]/20"}
      ${className}
    `}
      {...rest}
    />
  ),
);
TextInput.displayName = "TextInput";

// ─────────────────────────────────────────────
// Select
// ─────────────────────────────────────────────

export const Select = memo(
  ({
    id,
    value,
    onChange,
    options = [],
    placeholder,
    error,
    disabled = false,
    className = "",
  }) => (
    <select
      id={id}
      value={value}
      onChange={onChange}
      disabled={disabled}
      aria-invalid={!!error}
      aria-describedby={error ? `${id}-error` : undefined}
      className={`
      block w-full px-4 py-3 bg-[#e5e9e9] border-none rounded-xl
      text-[#181c1d] outline-none focus:bg-white focus:ring-2 transition-all
      disabled:opacity-50 disabled:cursor-not-allowed
      ${error ? "focus:ring-red-400" : "focus:ring-[#005b60]/20"}
      ${className}
    `}
    >
      {placeholder && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  ),
);
Select.displayName = "Select";

// ─────────────────────────────────────────────
// SearchInput
// ─────────────────────────────────────────────

export const SearchInput = memo(
  ({ value, onChange, placeholder = "Search…", className = "" }) => (
    <div className={`relative ${className}`}>
      <Icon
        name="search"
        className="absolute left-3 top-1/2 -translate-y-1/2 text-[#3e494a] text-[20px] pointer-events-none"
      />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="w-full bg-[#e5e9e9] border-none rounded-lg pl-10 pr-4 py-2.5 text-[#181c1d] placeholder:text-[#bdc9c9] outline-none focus:ring-2 focus:ring-[#005b60]/20 focus:bg-white transition-all"
      />
    </div>
  ),
);
SearchInput.displayName = "SearchInput";

// ─────────────────────────────────────────────
// InlineAlert
// ─────────────────────────────────────────────

const ALERT_STYLES = {
  success: { wrap: "text-[#436900] bg-[#bbf46e]/20", icon: "check_circle" },
  error: { wrap: "text-red-700 bg-red-50", icon: "error" },
  warning: { wrap: "text-amber-700 bg-amber-50", icon: "warning" },
  info: { wrap: "text-[#005b60] bg-[#e5f7f6]", icon: "info" },
};

export const InlineAlert = memo(({ type = "info", message }) => {
  if (!message) return null;
  const { wrap, icon } = ALERT_STYLES[type] ?? ALERT_STYLES.info;
  return (
    <div
      className={`flex items-center gap-2 px-4 py-3 rounded-xl ${wrap}`}
      role={type === "error" || type === "warning" ? "alert" : "status"}
    >
      <Icon name={icon} className="text-[20px] shrink-0" filled />
      <span className="text-sm font-semibold">{message}</span>
    </div>
  );
});
InlineAlert.displayName = "InlineAlert";

// ─────────────────────────────────────────────
// DrawerShell
// ─────────────────────────────────────────────

export const DrawerShell = memo(
  ({ isOpen, onClose, title, subtitle, icon, children, footer }) => {
    useEffect(() => {
      if (!isOpen) return;
      const handler = (e) => {
        if (e.key === "Escape") onClose?.();
      };
      document.addEventListener("keydown", handler);
      return () => document.removeEventListener("keydown", handler);
    }, [isOpen, onClose]);

    return (
      <>
        {isOpen && (
          <div
            className="fixed inset-0 bg-[#181c1d]/20 backdrop-blur-sm z-[60]"
            aria-hidden="true"
            onClick={onClose}
          />
        )}
        <aside
          role="dialog"
          aria-modal="true"
          aria-label={title}
          className={`fixed top-0 right-0 h-full w-[480px] bg-white z-[70] shadow-[-20px_0px_40px_rgba(24,28,29,0.06)] flex flex-col transform transition-transform duration-300 ${isOpen ? "translate-x-0" : "translate-x-full"}`}
        >
          <div className="px-8 py-6 flex items-center justify-between border-b border-[#bdc9c9]/20 shrink-0">
            <div className="flex items-start gap-3">
              {icon && (
                <span className="bg-[#005b60]/10 text-[#005b60] p-2 rounded-lg shrink-0">
                  <Icon name={icon} className="text-[20px]" />
                </span>
              )}
              <div>
                <h2 className="text-xl font-bold text-[#181c1d]">{title}</h2>
                {subtitle && (
                  <p className="text-sm text-[#3e494a] mt-0.5">{subtitle}</p>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close panel"
              className="text-[#6e797a] hover:text-[#181c1d] transition-colors p-2 hover:bg-[#e5e9e9] rounded-full ml-4 shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#005b60]"
            >
              <Icon name="close" className="text-[22px]" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-8 py-6">{children}</div>
          {footer && (
            <div className="px-8 py-5 flex items-center gap-3 bg-white border-t border-[#bdc9c9]/20 shrink-0">
              {footer}
            </div>
          )}
        </aside>
      </>
    );
  },
);
DrawerShell.displayName = "DrawerShell";

const CONFIRM_VARIANTS = {
  danger: {
    iconName: "delete_forever",
    iconStyle: "text-red-600 bg-red-50",
    btnStyle:
      "bg-red-600 hover:bg-red-700 focus-visible:ring-red-400 text-white",
  },
  warning: {
    iconName: "warning",
    iconStyle: "text-amber-600 bg-amber-50",
    btnStyle:
      "bg-amber-500 hover:bg-amber-600 focus-visible:ring-amber-400 text-white",
  },
  info: {
    iconName: "info",
    iconStyle: "text-[#005b60] bg-[#e5f7f6]",
    btnStyle:
      "bg-[#005b60] hover:bg-[#004f54] focus-visible:ring-[#005b60] text-white",
  },
};

export const ConfirmDialog = memo(
  ({
    isOpen,
    title = "Confirm Deletion",
    message,
    confirmLabel = "Yes, Delete",
    cancelLabel = "Cancel",
    variant = "danger",
    isLoading = false,
    onConfirm,
    onCancel,
    errorMessage = null,
  }) => {
    useEffect(() => {
      if (!isOpen) return;
      const handler = (e) => {
        if (e.key === "Escape" && !isLoading) onCancel?.();
      };
      document.addEventListener("keydown", handler);
      return () => document.removeEventListener("keydown", handler);
    }, [isOpen, isLoading, onCancel]);

    if (!isOpen) return null;

    const { iconName } = CONFIRM_VARIANTS[variant] ?? CONFIRM_VARIANTS.danger;

    const defaultDeleteMessage = (
      <>
        <p className="text-sm text-[#181c1d]">
          Are you sure you want to delete this?
        </p>
        <p className="text-sm text-[#6e797a] mt-1">
          You can't revert this action.
        </p>
      </>
    );

    return (
      <>
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[80]"
          onClick={() => !isLoading && onCancel?.()}
        />

        {/* Dialog */}
        <div
          role="alertdialog"
          aria-modal="true"
          className="fixed z-[90] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2
                   w-[calc(100%-2rem)] max-w-[440px]
                   bg-white rounded-3xl shadow-2xl overflow-hidden"
          style={{ animation: "cdIn 0.2s ease-out" }}
        >
          {/* Body */}
          <div className="px-8 pt-8 pb-6 text-center">
            {/* Icon */}
            <div
              className="mx-auto mb-4 w-12 h-12 rounded-2xl bg-red-50
                          flex items-center justify-center"
            >
              <Icon
                name={iconName}
                className="text-red-600 text-[24px]"
                filled
              />
            </div>

            {/* Title */}
            <h3 className="text-lg font-bold text-[#181c1d] mb-2">{title}</h3>

            {/* Message */}
            {!errorMessage && (
              <div className="text-sm leading-relaxed">
                {message ?? defaultDeleteMessage}
              </div>
            )}

            {/* Mapped error banner */}
            {errorMessage && (
              <div className="mt-4 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-left">
                <Icon
                  name="warning"
                  className="text-amber-500 text-[18px] shrink-0 mt-0.5"
                  filled
                />
                <p className="text-sm text-amber-800">{errorMessage}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-[#f7fafa] flex justify-center gap-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="px-6 py-3 rounded-full text-sm font-semibold
                       bg-[#e5e9e9] text-[#3e494a]
                       hover:bg-[#d0d8d8]
                       transition disabled:opacity-50"
            >
              {cancelLabel}
            </button>

            {!errorMessage && (
              <button
                type="button"
                onClick={onConfirm}
                disabled={isLoading}
                className="px-6 py-3 rounded-full text-sm font-semibold
                         bg-red-600 text-white
                         hover:bg-red-700
                         transition disabled:opacity-50
                         inline-flex items-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Spinner size="w-4 h-4" color="text-white" />
                    Deleting…
                  </>
                ) : (
                  confirmLabel
                )}
              </button>
            )}
          </div>
        </div>

        {/* Animation */}
        <style>{`
        @keyframes cdIn {
          from {
            opacity: 0;
            transform: translate(-50%, -45%) scale(0.96);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }
      `}</style>
      </>
    );
  },
);

ConfirmDialog.displayName = "ConfirmDialog";

// ─────────────────────────────────────────────
// StatCard
// ─────────────────────────────────────────────

export const StatCard = memo(({ label, value, plain = false }) => (
  <div className="flex justify-between items-center">
    <dt className="text-sm font-medium text-[#181c1d]">{label}</dt>
    <dd>
      {plain ? (
        <span className="text-xs font-bold text-[#3e494a]">{value}</span>
      ) : (
        <span className="px-2 py-1 bg-white rounded-lg text-xs font-bold text-[#005b60] shadow-sm">
          {value}
        </span>
      )}
    </dd>
  </div>
));
StatCard.displayName = "StatCard";

// ─────────────────────────────────────────────
// ProgressBar
// ─────────────────────────────────────────────

export const ProgressBar = memo(
  ({ value = 0, max = 100, label, colorClass = "bg-[#bbf46e]" }) => (
    <div
      className="w-full bg-white/10 rounded-full h-2"
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-label={label}
    >
      <div
        className={`${colorClass} h-full rounded-full transition-all`}
        style={{ width: `${Math.min(100, (value / max) * 100)}%` }}
      />
    </div>
  ),
);
ProgressBar.displayName = "ProgressBar";

export const Dropdown = memo(
  ({
    options = [],
    value,
    onChange,
    placeholder = "Select…",
    searchPlaceholder = "Search…",
    multi = false,
    error,
    disabled = false,
    maxHeight = "max-h-56",
  }) => {
    const [open, setOpen] = useState(false);
    const [openUpward, setOpenUpward] = useState(false);
    const [search, setSearch] = useState("");
    const containerRef = useRef(null);
    const searchRef = useRef(null);

    // Flips the panel above the trigger when there isn't room below —
    // otherwise a Dropdown near the bottom of a scrollable container (e.g.
    // the last field in a scrolling modal) renders its panel past the
    // visible/scrollable bounds and it's effectively invisible.
    const toggleOpen = () => {
      if (disabled) return;
      if (!open && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const estimatedPanelHeight = 320; // search box + options list, worst case
        const spaceBelow = window.innerHeight - rect.bottom;
        setOpenUpward(spaceBelow < estimatedPanelHeight && rect.top > spaceBelow);
      }
      setOpen((p) => !p);
    };

    // Close on outside click
    useEffect(() => {
      const handler = (e) => {
        if (containerRef.current && !containerRef.current.contains(e.target)) {
          setOpen(false);
          setSearch("");
        }
      };
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    }, []);

    // Focus search when opened
    useEffect(() => {
      if (open) setTimeout(() => searchRef.current?.focus(), 30);
    }, [open]);

    const filtered = options.filter((o) =>
      o.label.toLowerCase().includes(search.toLowerCase()),
    );

    // ── Single select helpers ─────────────────────
    const selectedOption = !multi
      ? options.find((o) => String(o.value) === String(value))
      : null;

    const handleSingleSelect = useCallback(
      (opt) => {
        onChange(opt.value);
        setOpen(false);
        setSearch("");
      },
      [onChange],
    );

    // ── Multi select helpers ──────────────────────
    const selectedValues = multi ? (Array.isArray(value) ? value : []) : [];

    const handleMultiToggle = useCallback(
      (opt) => {
        const strVal = String(opt.value);
        const current = selectedValues.map(String);
        if (current.includes(strVal)) {
          onChange(selectedValues.filter((v) => String(v) !== strVal));
        } else {
          onChange([...selectedValues, opt.value]);
        }
      },
      [selectedValues, onChange],
    );

    const removeTag = useCallback(
      (val, e) => {
        e.stopPropagation();
        onChange(selectedValues.filter((v) => String(v) !== String(val)));
      },
      [selectedValues, onChange],
    );

    // ── Display label ─────────────────────────────
    const displayLabel = () => {
      if (!multi) {
        return selectedOption ? (
          <span className="text-[#181c1d]">{selectedOption.label}</span>
        ) : (
          <span className="text-[#3e494a]/50">{placeholder}</span>
        );
      }
      if (!selectedValues.length) {
        return <span className="text-[#3e494a]/50">{placeholder}</span>;
      }
      const tags = options.filter((o) =>
        selectedValues.map(String).includes(String(o.value)),
      );
      return (
        <div className="flex flex-wrap gap-1">
          {tags.map((t) => (
            <span
              key={t.value}
              className="inline-flex items-center gap-1 bg-[#08757c]/10 text-[#08757c]
                       text-xs font-semibold px-2 py-0.5 rounded-full"
            >
              {t.label}
              <button
                type="button"
                onClick={(e) => removeTag(t.value, e)}
                className="hover:text-red-500 transition-colors"
                aria-label={`Remove ${t.label}`}
              >
                <span className="material-symbols-outlined text-[12px]">
                  close
                </span>
              </button>
            </span>
          ))}
        </div>
      );
    };

    return (
      <div ref={containerRef} className="relative w-full">
        {/* Trigger */}
        <button
          type="button"
          disabled={disabled}
          onClick={toggleOpen}
          className={`
          w-full flex items-center justify-between gap-2
          bg-[#e5e9e9] rounded-xl px-4 py-4 text-sm text-left
          transition-all outline-none
          focus:ring-2 focus:ring-[#08757c] focus:bg-white
          disabled:opacity-50 disabled:cursor-not-allowed
          ${error ? "ring-2 ring-[#ba1a1a]" : ""}
          ${open ? "ring-2 ring-[#08757c] bg-white" : ""}
        `}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className="flex-1 min-w-0">{displayLabel()}</span>
          <span
            className={`material-symbols-outlined text-[20px] text-[#3e494a] flex-shrink-0 transition-transform duration-200
            ${open ? "rotate-180" : ""}`}
          >
            expand_more
          </span>
        </button>

        {/* Error */}
        {error && <p className="text-xs text-[#ba1a1a] mt-1 ml-1">{error}</p>}

        {/* Dropdown panel */}
        {open && (
          <div
            className={`absolute z-[500] left-0 right-0
                     ${openUpward ? "bottom-full mb-1" : "top-full mt-1"}
                     bg-white rounded-xl shadow-xl border border-[#e5e9e9]
                     overflow-hidden`}
            role="listbox"
            aria-multiselectable={multi}
          >
            {/* Search box */}
            <div className="px-3 pt-3 pb-2 border-b border-[#f1f4f4]">
              <div className="relative">
                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[#3e494a]/50 text-[16px]">
                  search
                </span>
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="w-full pl-9 pr-3 py-2 text-sm bg-[#f1f4f4] rounded-lg
                           border-none outline-none focus:ring-2 focus:ring-[#08757c]/30
                           text-[#181c1d] placeholder:text-[#3e494a]/40"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#3e494a]/50 hover:text-[#181c1d]"
                  >
                    <span className="material-symbols-outlined text-[14px]">
                      close
                    </span>
                  </button>
                )}
              </div>
            </div>

            {/* Options list */}
            <ul className={`overflow-y-auto ${maxHeight} py-1`}>
              {filtered.length === 0 ? (
                <li className="px-4 py-3 text-sm text-[#3e494a]/60 text-center">
                  No results found
                </li>
              ) : (
                filtered.map((opt) => {
                  const isSelected = multi
                    ? selectedValues.map(String).includes(String(opt.value))
                    : String(opt.value) === String(value);

                  return (
                    <li
                      key={opt.value}
                      role="option"
                      aria-selected={isSelected}
                      onClick={() =>
                        multi ? handleMultiToggle(opt) : handleSingleSelect(opt)
                      }
                      className={`
                      flex items-center gap-3 px-4 py-2.5 text-sm cursor-pointer
                      transition-colors select-none
                      ${
                        isSelected
                          ? "bg-[#08757c]/8 text-[#08757c] font-semibold"
                          : "text-[#181c1d] hover:bg-[#f1f4f4]"
                      }
                    `}
                    >
                      {/* Checkbox for multi */}
                      {multi && (
                        <span
                          className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors
                          ${
                            isSelected
                              ? "bg-[#08757c] border-[#08757c]"
                              : "border-[#bdc9c9] bg-white"
                          }`}
                        >
                          {isSelected && (
                            <span className="material-symbols-outlined text-white text-[12px]">
                              check
                            </span>
                          )}
                        </span>
                      )}

                      <span className="flex-1">{opt.label}</span>

                      {/* Checkmark for single */}
                      {!multi && isSelected && (
                        <span className="material-symbols-outlined text-[#08757c] text-[18px]">
                          check
                        </span>
                      )}
                    </li>
                  );
                })
              )}
            </ul>

            {/* Multi select footer */}
            {multi && selectedValues.length > 0 && (
              <div className="px-4 py-2.5 border-t border-[#f1f4f4] flex items-center justify-between">
                <span className="text-xs text-[#3e494a]">
                  {selectedValues.length} selected
                </span>
                <button
                  type="button"
                  onClick={() => onChange([])}
                  className="text-xs text-[#ba1a1a] font-semibold hover:opacity-75 transition-opacity"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  },
);
Dropdown.displayName = "Dropdown ";

// ─────────────────────────────────────────────
// Textarea
// ─────────────────────────────────────────────

export const Textarea = memo(
  ({
    id,
    value,
    onChange,
    placeholder = "",
    rows = 5,
    error,
    disabled = false,
    className = "",
    maxLength,
    htmlValue = false,
  }) => (
    <div>
      <textarea
        id={id}
        value={value}
        onChange={(e) => {
          const val = e.target.value.replace(/^\s+/, "");
          const finalVal = htmlValue ? val.replace(/\n/g, "<br />") : val;
          debugger;
          onChange({ ...e, target: { ...e.target, value: finalVal } });
        }}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        maxLength={maxLength}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
        className={`
        w-full px-4 py-3 bg-[#e5e9e9] rounded-lg
        text-[#181c1d] placeholder:text-[#bdc9c9] outline-none
        focus:bg-white focus:ring-2 transition-all
        resize-y min-h-[80px]
        disabled:opacity-50 disabled:cursor-not-allowed
        border ${className}
        ${error ? "!border-2 !border-red-500" : "border-slate-200"}
      `}
      />
      {error && (
        <p
          id={`${id}-error`}
          className="text-xs text-red-500 mt-1"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  ),
);
Textarea.displayName = "Textarea";

// useAuth.js

export const useAuth = () => {
  debugger;
  const { user } = useSelector((state) => state.auth);

  return {
    employeeID: user?.employeeID || 0,
    companyID: user?.CompanyId || 0,
    user,
  };
};

// ─────────────────────────────────────────────
// FileUpload
// ─────────────────────────────────────────────

export const FileUpload = memo(
  ({
    value,
    onChange,
    error,
    multiple = false,
    accept = "image/png,image/jpg,image/jpeg",
    hint = "PNG, JPG, JPEG up to 10MB",
  }) => {
    const inputRef = useRef(null);

    // ── Handlers ─────────────────────────────────
    const handleFileChange = (e) => {
      const files = Array.from(e.target.files ?? []);
      if (!files.length) return;
      if (multiple) {
        onChange([...(Array.isArray(value) ? value : []), ...files]);
      } else {
        onChange(files[0]);
      }
    };

    const handleRemoveSingle = () => {
      onChange(null);
      if (inputRef.current) inputRef.current.value = "";
    };

    const handleRemoveMultiple = (index) => {
      onChange(
        (Array.isArray(value) ? value : []).filter((_, i) => i !== index),
      );
    };

    const handleDrop = (e) => {
      e.preventDefault();
      const files = Array.from(e.dataTransfer.files ?? []);
      if (!files.length) return;
      if (multiple) {
        onChange([...(Array.isArray(value) ? value : []), ...files]);
      } else {
        onChange(files[0]);
      }
    };

    // ── Derived ───────────────────────────────────
    const hasFile = multiple
      ? Array.isArray(value) && value.length > 0
      : !!value;
    const showZone = multiple ? true : !hasFile; // multiple always shows drop zone

    return (
      <div>
        {/* Drop zone */}
        {showZone && (
          <div
            onClick={() => inputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            className={`w-full border-2 border-dashed rounded-xl p-6 text-center cursor-pointer
                      transition-all hover:border-[#08757c] hover:bg-[#08757c]/5
                      ${error ? "border-[#ba1a1a] bg-red-50" : "border-[#bdc9c9] bg-[#e5e9e9]/50"}`}
          >
            <span className="material-symbols-outlined text-[40px] text-[#3e494a]/50 mb-2 block">
              upload_file
            </span>
            <p className="text-sm font-semibold text-[#3e494a]">
              Click to upload or drag & drop
            </p>
            <p className="text-xs text-[#3e494a]/60 mt-1">{hint}</p>
            <input
              ref={inputRef}
              type="file"
              accept={accept}
              multiple={multiple}
              className="hidden"
              onChange={handleFileChange}
            />
          </div>
        )}

        {/* ── Single file preview ── */}
        {!multiple && hasFile && (
          <div className="flex items-center justify-between bg-white px-4 py-2.5 rounded-xl border border-[#bdc9c9]/30 shadow-sm">
            <div className="flex items-center gap-3 min-w-0">
              <span className="material-symbols-outlined text-[#08757c] text-[20px] flex-shrink-0">
                image
              </span>
              <span className="text-sm text-[#181c1d] truncate">
                {value.name}
              </span>
              <span className="text-xs text-[#3e494a]/60 flex-shrink-0">
                {(value.size / 1024).toFixed(0)} KB
              </span>
            </div>
            <button
              type="button"
              onClick={handleRemoveSingle}
              className="text-[#3e494a]/50 hover:text-[#ba1a1a] transition-colors ml-3 flex-shrink-0"
              aria-label="Remove file"
            >
              <span className="material-symbols-outlined text-[18px]">
                close
              </span>
            </button>
          </div>
        )}

        {/* ── Multiple file list ── */}
        {multiple && hasFile && (
          <ul className="mt-3 space-y-2">
            {(Array.isArray(value) ? value : []).map((file, i) => (
              <li
                key={i}
                className="flex items-center justify-between bg-white px-4 py-2.5 rounded-xl border border-[#bdc9c9]/30 shadow-sm"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="material-symbols-outlined text-[#08757c] text-[20px] flex-shrink-0">
                    image
                  </span>
                  <span className="text-sm text-[#181c1d] truncate">
                    {file.name}
                  </span>
                  <span className="text-xs text-[#3e494a]/60 flex-shrink-0">
                    {(file.size / 1024).toFixed(0)} KB
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveMultiple(i)}
                  className="text-[#3e494a]/50 hover:text-[#ba1a1a] transition-colors ml-3 flex-shrink-0"
                  aria-label={`Remove ${file.name}`}
                >
                  <span className="material-symbols-outlined text-[18px]">
                    close
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {error && <p className="text-xs text-[#ba1a1a] mt-1 ml-1">{error}</p>}
      </div>
    );
  },
);
FileUpload.displayName = "FileUpload";

// ─────────────────────────────────────────────
// ExportExcel
// Props:
//   headers  – string[]          column header labels
//   data     – any[][]           rows of cell values (same order as headers)
//   filename – string            download filename (no extension)
//   title    – string (optional) bold title row above headers
//   buttonLabel – string         button text  (default "Export Excel")
//   disabled – bool
//   className – string
// ─────────────────────────────────────────────
export const ExportExcel = memo(
  ({
    headers = [],
    data = [],
    filename = "export",
    buttonLabel = "Export Excel",
    disabled = false,
    className = "",
  }) => {
    const [exporting, setExporting] = useState(false);

    async function buildAndDownload() {
      if (!headers.length || !data.length) return;
      setExporting(true);
      try {
        if (!window.ExcelJS) {
          await new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src =
              "https://cdn.jsdelivr.net/npm/exceljs@4.4.0/dist/exceljs.min.js";
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }

        const workbook = new window.ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Data");

        worksheet.columns = headers.map((h) => ({
          width: Math.min(Math.max(h.length + 4, 14), 60),
        }));

        const thin = { style: "thin", color: { argb: "FF000000" } };
        const border = { top: thin, left: thin, bottom: thin, right: thin };

        // Header row — yellow, bold, black border, wrap text
        const headerRow = worksheet.addRow(headers);
        for (let c = 1; c <= headers.length; c++) {
          const cell = headerRow.getCell(c);
          cell.font = { bold: true, color: { argb: "FF000000" } };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFFFFF00" },
          };
          cell.border = border;
          cell.alignment = {
            vertical: "middle",
            horizontal: "left",
            wrapText: true,
          };
        }

        // Data rows — black border, wrap text
        data.forEach((row) => {
          const dataRow = worksheet.addRow(row.map((v) => v ?? ""));
          for (let c = 1; c <= headers.length; c++) {
            const cell = dataRow.getCell(c);
            cell.border = border;
            cell.alignment = { vertical: "middle", wrapText: true };
          }
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${filename}_${new Date().toISOString().slice(0, 10)}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch {
        // Fallback — HTML/XLS
        const BORDER = "border:1px solid #000000;";
        const CELL =
          "padding:7px 10px;font-family:Arial,sans-serif;font-size:11px;word-wrap:break-word;";
        const HEAD_BG =
          "background-color:#FFFF00;color:#000000;font-weight:bold;text-align:left;";
        let html = `<html xmlns:o="urn:schemas-microsoft-com:office:office"
        xmlns:x="urn:schemas-microsoft-com:office:excel"
        xmlns="http://www.w3.org/TR/REC-html40">
        <head><meta charset="UTF-8"/></head><body><table style="border-collapse:collapse;">`;
        html += `<tr>${headers.map((h) => `<th style="${BORDER}${CELL}${HEAD_BG}">${h}</th>`).join("")}</tr>`;
        data.forEach((row) => {
          html += `<tr>${row.map((cell) => `<td style="${BORDER}${CELL}color:#1a1a1a;">${cell ?? ""}</td>`).join("")}</tr>`;
        });
        html += `</table></body></html>`;
        const blob = new Blob([html], {
          type: "application/vnd.ms-excel;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${filename}.xls`;
        a.click();
        URL.revokeObjectURL(url);
      } finally {
        setExporting(false);
      }
    }

    return (
      <button
        type="button"
        onClick={buildAndDownload}
        disabled={disabled || !data.length || exporting}
        className={`bg-[#bbf46e] text-[#112000] px-6 py-2.5 rounded-lg text-sm font-semibold
        hover:opacity-90 transition-opacity flex items-center gap-2 disabled:opacity-50 ${className}`}
      >
        <span className="material-symbols-outlined text-sm">
          {exporting ? "hourglass_empty" : "download"}
        </span>
        {exporting ? "Exporting…" : buttonLabel}
      </button>
    );
  },
);
ExportExcel.displayName = "ExportExcel";

export const employeeID = Number(localStorage.getItem("empid")) || 0;
export const employeeName = localStorage.getItem("empName") || "";
export const roleName = localStorage.getItem("roleName") || "";
export const roleCode = localStorage.getItem("roleCode") || "";

export const companyID = Number(localStorage.getItem("companyId")) || 0;
export const UserID = Number(localStorage.getItem("UserId")) || 0;

export const getEmployeeID = () => localStorage.getItem("empid");
export const getCompanyID = () => localStorage.getItem("companyId");

// ─── Lab / Facility name character restriction ────────────────────────────────
// Allowed: letters, digits, space, hyphen, underscore, ampersand, parentheses,
//          forward-slash, and period.
// Blocked: @ # $ % ^ * < > ' " ; -- and all other special characters.
export const VALID_NAME_REGEX = /^[a-zA-Z0-9 \-_&()/.]*$/;

// Strips every disallowed character AND leading spaces from the raw input string.
// Use in onChange: e.g. onChange={e => setName(sanitizeLabName(e.target.value))}
export const sanitizeLabName = (value) =>
  value.replace(/^\s+/, "").replace(/[^a-zA-Z0-9 \-_&()/. ]/g, "");

// Sanitize Client ID: only alphanumeric and hyphens
export const sanitizeClientId = (value) => value.replace(/[^a-zA-Z0-9\-]/g, "");

// Sanitize Email Domain: only alphanumeric, dots, and hyphens
export const sanitizeEmailDomain = (value) =>
  value.replace(/[^a-zA-Z0-9.\-]/g, "");

// Normalize spaces: trim leading/trailing spaces and collapse multiple spaces to single space
// Use in onChange: e.g. onChange={e => setName(normalizeSpaces(e.target.value))}
export const normalizeSpaces = (value) =>
  String(value).trim().replace(/\s+/g, " ");

// Returns an error message if the value contains disallowed characters,
// or an empty string if the value is valid.
// Use for submit-time validation as a second safety net.
export const validateLabName = (value) =>
  VALID_NAME_REGEX.test(value)
    ? ""
    : "Lab name contains invalid characters. Allowed: letters, numbers, spaces, and - _ & ( ) / .";

// Trims leading and trailing spaces from all string fields in a form object.
// Use before saving: e.g. const payload = trimFormValues(form);
export const trimFormValues = (form) =>
  Object.fromEntries(
    Object.entries(form).map(([k, v]) => [
      k,
      typeof v === "string" ? v.trim() : v,
    ]),
  );

// ─────────────────────────────────────────────
// KPICard — Reusable dashboard KPI metric card
// ─────────────────────────────────────────────

export const KPICard = memo(
  ({ icon, label, value, iconColor, gradientFrom, gradientTo, isLoading }) => (
    <div
      className={`group relative overflow-hidden rounded-2xl p-6 transition-all duration-500 transform hover:scale-105 hover:shadow-2xl bg-white border border-gray-100`}
    >
      {/* Gradient accent line */}
      <div
        className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${gradientFrom} ${gradientTo}`}
      ></div>

      {/* Subtle background gradient on hover */}
      <div
        className={`absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity duration-500 bg-gradient-to-br ${gradientFrom} ${gradientTo}`}
      ></div>

      {/* Content */}
      <div className="relative z-10">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-600 mb-2 tracking-wide uppercase">
              {label}
            </p>
            <p
              className={`text-4xl font-bold transition-all duration-300 ${iconColor} ${isLoading ? "animate-pulse blur-sm" : "blur-0"}`}
            >
              {isLoading
                ? "—"
                : typeof value === "number"
                  ? value.toLocaleString()
                  : value}
            </p>
          </div>

          {/* Icon with gradient background */}
          <div
            className={`p-3 rounded-xl bg-gradient-to-br ${gradientFrom} ${gradientTo} shadow-lg transform transition-transform duration-300 group-hover:scale-110`}
          >
            <span className={`material-symbols-outlined text-2xl text-white`}>
              {icon}
            </span>
          </div>
        </div>

        {/* Live data indicator */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <span className="material-symbols-outlined text-xs">
              trending_up
            </span>
            <span>Live data</span>
          </div>
        </div>
      </div>

      {/* Shine effect */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-500 bg-gradient-to-r from-transparent via-white to-transparent pointer-events-none"></div>
    </div>
  ),
);
KPICard.displayName = "KPICard";

export const PRIVILEGES = {
  EMP_VIEW: "EMPVIEW",
  EMP_CREATE: "EMPCREATE",
  EMP_EDIT: "EMPEDIT",
  EMP_DELETE: "EMPDELETE",

  ROLE_VIEW: "ROLEVIEW",
  ROLE_CREATE: "ROLECREATE",
  ROLE_EDIT: "ROLEEDIT",
  ROLE_DELETE: "ROLEDELETE",

  PROJ_VIEW: "PROJVIEW",
  PROJ_CREATE: "PROJCREATE",
  PROJ_EDIT: "PROJEDIT",

  CONFIG_VIEW: "APPCONFIGVIEW",

  EMAIL_VIEW: "EMAILTEMPLATEVIEW",
  EMAIL_EDIT: "EMAILTEMPLATEEDIT",

  SU_VIEW: "SUVIEW",
  SU_CREATE: "SUCREATE",
  SU_EDIT: "SUEDIT",
  LAB_VIEW: "LABVIEW",
  MP_VIEW: "MPVIEW",
  MP_CREATE: "MPCREATE",
  MP_EDIT: "MPEDIT",
  MP_DELETE: "MPDELETE",
};

export function hasPrivilege(privilegeCode) {
  try {
    const raw = localStorage.getItem("Privillages");
    if (!raw) return false;

    if (!raw.trim().startsWith("[") && !raw.trim().startsWith("{")) {
      return raw
        .split(",")
        .map((p) => p.trim())
        .includes(privilegeCode);
    }

    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.some((p) =>
        typeof p === "string"
          ? p === privilegeCode
          : p?.PrivilegeCode === privilegeCode || p?.Code === privilegeCode,
      );
    }

    return false;
  } catch {
    return false;
  }
}

export function isSmileIntegrated() {
  try {
    return localStorage.getItem("isFromSmile") === "1";
  } catch (err) {
    console.error("Error reading Smile integration flag:", err);
    return false;
  }
}
